import { useQuery, useMutation } from 'react-query';

import {
  useCurrentAddressInfo,
  useHiChatLocalUserInfo,
  useHiUserCache,
  useOfficialAccount,
  useUserInfo,
} from '@/hooks';
import {
  request,
  convertOfficialHiIdToOriginalId,
  getIsOfficialAccountById,
  isSystemAddress,
  noop,
} from '@/utils';
import { useRef } from 'react';
import type { HiUserInfo } from '../types';

export type UserCardShape = ReturnType<typeof useFullHiUserInfoByAddr>;

function useExtraInfo(address: string, pageKey: string | number) {
  const { address: currentUserId } = useCurrentAddressInfo();

  return {
    currentUserId,
  };
}

export const useHiUnreadMessageCount = (refetchInterval?: number) => {
  const { address: meId, isVerified } = useCurrentAddressInfo();
  const { putHiChatLocalUserInfo } = useHiChatLocalUserInfo();
  return useQuery(
    ['/hi/unread_message_count', meId],
    async () => {
      const result = await request.get<Pick<HiUserInfo, 'unread_message_count'>>(
        '/hi/unread_message_count',
      );
      result && putHiChatLocalUserInfo(result.unread_message_count);

      return result;
    },
    { enabled: Boolean(isVerified && meId), refetchInterval },
  );
};

export function useFullHiUserInfoByAddr(user_addr: string) {
  const pageKey = useRef(Math.random());
  const isOfficialAccount = getIsOfficialAccountById(user_addr);
  const isSystemAccount = isSystemAddress(user_addr);

  const { userDict } = useHiUserCache();

  const cacheUser = user_addr ? (userDict[user_addr] as UserInfo | OfficialAccount) : undefined;

  const { officialAccountDetail: officialAccount, ...officialAccountQuery } = useOfficialAccount(
    convertOfficialHiIdToOriginalId(user_addr),
  );

  const { data, ...userQuery } = useUserInfo(user_addr);

  const extraInfo = useExtraInfo(user_addr, pageKey.current);
  const userInfo =
    isSystemAccount || isOfficialAccount ? undefined : data || (cacheUser as UserInfo);

  return {
    pageKey,
    address: user_addr,
    userInfo,
    initialPrice: userInfo?.initial_price,
    offerPrice: userInfo?.offer_price,
    unchargedOfferValue: userInfo?.uncharged_offer_value,
    unchargedOfferCount: userInfo?.uncharged_offer_count,
    repliedRate: userInfo?.replied_rate,
    refetchHiUserInfo: isSystemAccount
      ? noop
      : isOfficialAccount
        ? officialAccountQuery.refetch
        : userQuery.refetch,
    officialAccount: isOfficialAccount
      ? officialAccount || (cacheUser as OfficialAccount)
      : undefined,

    loadingStatus: {
      userInfo:
        !cacheUser && (isOfficialAccount ? officialAccountQuery.isLoading : userQuery.isLoading),
    },
    fetchingStatus: {
      userInfo: isOfficialAccount ? officialAccountQuery.isFetching : userQuery.isFetching,
    },
    ...extraInfo,
  };
}

export function useSetBasePrice() {
  return useMutation(async (price: number) => {
    const result = await request.put<{ initial_price: number }>('/hi/user/set_initial_price', {
      initial_price: price,
    });
    return result;
  });
}
